import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { tokenReceived, logout, fetchMe } from '../store/slices/authSlice';
import {
  readTokenFromQuery,
  getStoredToken,
  requestTokenFromParent,
  isEmbedded,
} from '../utils/iframe';

/**
 * iframe bridge custom event'lerini Redux'a bağlar.
 * - re:auth:token → token'ı store'a işle
 * - re:auth:logout → store'u temizle
 * - re:navigate → router'ı yönlendir
 *
 * fetchMe yalnızca bir kez (mount'ta) çağrılır; her route değişiminde
 * yeniden tetiklenmez. Zaten her endpoint token'ı kendi başına doğrular;
 * 401 aldığında apiClient 'auth:expired' olayını üst pencereye iletir.
 */
export function useIframeAuth() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  // navigate ref — event handler'da her zaman güncel navigate'i kullanmak için,
  // ama deps dizisini şişirmeden.
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; });

  // fetchMe'nin yalnızca bir kez çalışmasını garanti eden guard.
  const didInit = useRef(false);

  useEffect(() => {
    // ── İlk çalışma: token al + /me çek ─────────────────────────────────
    if (!didInit.current) {
      didInit.current = true;

      // Token önceliği: URL query (dev) → sessionStorage (refresh sonrası mevcut).
      const initial = readTokenFromQuery() || getStoredToken();
      if (initial) {
        dispatch(tokenReceived(initial));
        dispatch(fetchMe());
      } else if (isEmbedded()) {
        // iframe açılış yarışı: parent'ın `auth:token` postMessage'ı app mount'u
        // ile yarışır; token henüz yokken doğrudan fetchMe atarsak 401 → logout
        // → "Erişim Reddedildi"/login ekranı görünür (intermittent "yükleniyorda
        // kalıyor / login'e gidiyor" şikayeti). Bunun yerine parent'tan token
        // iste; gelince token'ı işleyip /me çek. Bekleyen, parent'ın ister
        // `auth:request`'e cevabıyla ister `ready` anında gönderdiği `auth:token`
        // ile çözülür. Gelmezse (timeout) sessizce geç — `re:auth:token` event'i
        // ya da apiClient 401 refresh+retry akışı devreye girer.
        requestTokenFromParent()
          .then((tok) => {
            if (tok) {
              dispatch(tokenReceived(tok));
              dispatch(fetchMe());
            }
          })
          .catch(() => { /* token gelmedi — event/401 akışı ele alır */ });
      } else {
        // Standalone/dev — token yoksa fetchMe 401 döner (normal akış).
        dispatch(fetchMe());
      }
    }

    // ── iframe olayları ──────────────────────────────────────────────────
    const onToken = (e) => {
      const token = e.detail?.token;
      if (token) {
        dispatch(tokenReceived(token));
        // Yeni token geldiğinde kullanıcı bilgisini güncelle.
        dispatch(fetchMe());
      }
    };
    const onLogout   = () => dispatch(logout());
    const onNavigate = (e) => {
      const path = e.detail?.path;
      if (path) navigateRef.current(path);
    };
    // apiClient'tan gelen 401 sinyali: spine cevap vermese bile yerel token'ı
    // temizle → kullanıcı auth-blocked ekranını görür. Spine sonradan yeni
    // token gönderirse 're:auth:token' ile kaldığı yerden devam eder.
    const on401 = () => dispatch(logout());

    window.addEventListener('re:auth:token',  onToken);
    window.addEventListener('re:auth:logout', onLogout);
    window.addEventListener('re:navigate',    onNavigate);
    window.addEventListener('re:auth:401',    on401);

    return () => {
      window.removeEventListener('re:auth:token',  onToken);
      window.removeEventListener('re:auth:logout', onLogout);
      window.removeEventListener('re:navigate',    onNavigate);
      window.removeEventListener('re:auth:401',    on401);
    };
  // dispatch Redux garantisi gereği stabildir; navigate artık ref üzerinden
  // erişildiği için deps dizisine eklenmesine gerek yok.
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps
}
